/**
 * Time helpers for the student views.
 *
 * Every day in this feature is an Asia/Tashkent calendar day (the day streaks and the activity
 * filters use), so instants are formatted in that zone whatever the browser's zone is, and "now" is
 * the server's `meta.generatedAt`, not the browser clock: a page left open does not drift and the
 * words match the filters ("not seen for 7 days" is `daysSinceSeen >= 7`).
 */

import { formatDate as dtFormatDate, formatDateTime as dtFormatDateTime, formatTime as dtFormatTime, toYmd } from '../../utils/datetime';

export const STUDENT_TIME_ZONE = 'Asia/Tashkent';

// Dates are written by utils/datetime.js (Uzbek first, 24-hour clock); this feature only pins the
// zone. `lang` is optional: the user's language when omitted.
const IN_TASHKENT = { timeZone: STUDENT_TIME_ZONE };

function toDate(value) {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "17-oktabr 2026, 14:27" in Tashkent time; an em dash for a missing value. */
export function formatDateTime(value, lang) {
  return dtFormatDateTime(value, { ...IN_TASHKENT, lang, year: 'always' });
}

/** "17-oktabr 2026" in Tashkent time. */
export function formatDate(value, lang) {
  return dtFormatDate(value, { ...IN_TASHKENT, lang });
}

/** "14:27" in Tashkent time. */
export function formatClock(value, lang) {
  return dtFormatTime(value, { ...IN_TASHKENT, lang });
}

/** The Asia/Tashkent calendar day of an instant (or epoch ms) as 'YYYY-MM-DD'; null when invalid. */
export function tashkentDay(value) {
  return toYmd(value, IN_TASHKENT);
}

const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** A 'YYYY-MM-DD' Tashkent day as a readable date. It is a day, not an instant: no zone shift. */
export function formatDay(day, lang) {
  if (!DAY_PATTERN.test(day || '')) return '—';
  return dtFormatDate(day, { lang });
}

/** Whole calendar days from one 'YYYY-MM-DD' day to another (negative when `toDay` is earlier). */
export function dayDiff(fromDay, toDay) {
  if (!DAY_PATTERN.test(fromDay || '') || !DAY_PATTERN.test(toDay || '')) return null;
  return Math.round((Date.parse(`${toDay}T00:00:00Z`) - Date.parse(`${fromDay}T00:00:00Z`)) / 86400000);
}

/** The reference time of a response: its server clock, else the browser's. */
export function nowFrom(meta) {
  const parsed = meta?.generatedAt ? Date.parse(meta.generatedAt) : NaN;
  return Number.isNaN(parsed) ? Date.now() : parsed;
}

/**
 * How long ago an exact instant was, as a descriptor for agoText():
 * under a minute "justNow", under an hour minutes, under a day hours, then Tashkent calendar days
 * (`daysHint` is the server's own daysSinceSeen, used when given so the words match the filters).
 */
export function describeInstant(value, nowMs, daysHint = null) {
  const date = toDate(value);
  if (!date) return null;
  const elapsed = Math.max(0, nowMs - date.getTime());
  if (elapsed < 60000) return { kind: 'justNow' };
  if (elapsed < 3600000) return { kind: 'minutes', n: Math.floor(elapsed / 60000) };
  if (elapsed < 86400000) return { kind: 'hours', n: Math.floor(elapsed / 3600000) };
  const calendar = dayDiff(tashkentDay(date), tashkentDay(nowMs));
  const n = Number.isInteger(daysHint) && daysHint >= 1 ? daysHint : calendar;
  return { kind: 'days', n: Math.max(1, n ?? 1) };
}

/** How long ago a Tashkent calendar day was: today, yesterday or N days (no time of day exists). */
export function describeDay(day, nowMs) {
  if (!DAY_PATTERN.test(day || '')) return null;
  const n = dayDiff(day, tashkentDay(nowMs));
  if (n === null || n <= 0) return { kind: 'today' };
  if (n === 1) return { kind: 'yesterday' };
  return { kind: 'days', n };
}

/** The words for a descriptor of describeInstant() / describeDay(): "3 h ago", "yesterday". */
export function agoText(t, ago) {
  if (!ago) return '';
  return t(`student360.ago.${ago.kind}`, ago.n === undefined ? undefined : { n: ago.n });
}

/** First letter upper-case, for a time that stands alone in a cell ("Yesterday"); digits are left. */
export function ucfirst(text) {
  const value = String(text || '');
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

/**
 * Presence as one shape for both precisions, ready to render.
 *   tone  online | today | away (1-6 days) | cold (7 or more days, the at-risk threshold) | never
 *   ago   descriptor of the last seen (null when never seen)
 *   exact the instant (minute precision) or day (day precision) behind it, for the tooltip
 *   login / loginExact the same for the last sign-in; null until the user signs in once after the
 *         release, which is not the same as never seen
 */
export function presenceView(presence, nowMs) {
  if (!presence) return null;
  const days = Number.isInteger(presence.daysSinceSeen) ? presence.daysSinceSeen : null;

  if (presence.precision === 'day') {
    const ago = describeDay(presence.lastSeenDay, nowMs);
    return {
      precision: 'day',
      online: false,
      ago,
      exact: presence.lastSeenDay || null,
      approximate: presence.approximate === true,
      tone: toneOf(false, ago, days),
      login: describeDay(presence.lastLoginDay, nowMs),
      loginExact: presence.lastLoginDay || null,
    };
  }

  const online = presence.onlineNow === true;
  const ago = presence.lastSeenAt ? describeInstant(presence.lastSeenAt, nowMs, days) : null;
  return {
    precision: 'minute',
    online,
    ago,
    exact: presence.lastSeenAt || null,
    approximate: presence.approximate === true,
    tone: toneOf(online, ago, days),
    login: presence.lastLoginAt ? describeInstant(presence.lastLoginAt, nowMs) : null,
    loginExact: presence.lastLoginAt || null,
  };
}

function toneOf(online, ago, days) {
  if (online) return 'online';
  if (!ago) return 'never';
  // Under a day ago is "today" to the eye, whichever side of midnight it fell.
  if (['today', 'justNow', 'minutes', 'hours'].includes(ago.kind)) return 'today';
  const away = days !== null ? days : (ago.kind === 'yesterday' ? 1 : ago.n);
  return away >= 7 ? 'cold' : 'away';
}

/** 184 -> "3:04", 3725 -> "1:02:05": a video position in seconds. */
export function formatPosition(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}
